import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Users, Save } from "lucide-react";

export default function TeamDirectory() {
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/team"); setMembers(data.members || []); }
    catch { toast.error("Failed"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    try { await api.delete(`/team/${m.id}`); toast.success("Removed"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 lg:p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-caps mb-2">Admin · Access</div>
          <h1 className="font-display text-4xl font-medium tracking-tight text-slate-900">Team Directory</h1>
          <p className="text-slate-500 mt-3 max-w-lg">People you invite to your published dashboards. Attributes like <span className="mono">department</span> auto-fill row-level access rules.</p>
        </div>
        <button data-testid="add-member" onClick={() => setEditing({ new: true })} className="btn-primary"><Plus size={14} /> Add member</button>
      </div>

      {loading ? <div className="text-slate-400">Loading…</div>
      : members.length === 0 ? (
        <div className="card text-center py-16">
          <Users size={28} className="mx-auto text-slate-300 mb-4" />
          <div className="text-lg font-medium text-slate-900 mb-2">No members yet</div>
          <div className="text-slate-500 text-sm mb-6">Add people who should see your published dashboards.</div>
          <button onClick={() => setEditing({ new: true })} className="btn-primary"><Plus size={14} /> Add first member</button>
        </div>
      ) : (
        <div className="card-flush overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-[var(--border)]">
              <tr>
                <th className="text-left px-5 py-3 label-caps font-medium">Name</th>
                <th className="text-left px-5 py-3 label-caps font-medium">Email</th>
                <th className="text-left px-5 py-3 label-caps font-medium">Attributes</th>
                <th className="text-left px-5 py-3 label-caps font-medium">Notes</th>
                <th className="text-right px-5 py-3 label-caps font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-slate-100" data-testid={`member-${m.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{m.name}</td>
                  <td className="px-5 py-3 mono text-xs text-slate-500">{m.email}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(m.attributes || {}).map(([k, v]) => <span key={k} className="chip mono">{k}={v}</span>)}
                      {Object.keys(m.attributes || {}).length === 0 && <span className="text-xs text-slate-400">—</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500 truncate max-w-[220px]">{m.notes || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-3">
                      <button onClick={() => setEditing(m)} data-testid={`edit-${m.id}`} className="text-xs text-[var(--primary)] hover:underline">Edit</button>
                      <button onClick={() => del(m)} data-testid={`del-${m.id}`} className="text-slate-400 hover:text-[var(--danger)]"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <MemberDialog member={editing.new ? null : editing} onClose={() => setEditing(null)} onSaved={() => { load(); setEditing(null); }} />}
    </div>
  );
}

function MemberDialog({ member, onClose, onSaved }) {
  const [form, setForm] = useState({ email: member?.email || "", name: member?.name || "", notes: member?.notes || "" });
  const [attrs, setAttrs] = useState(Object.entries(member?.attributes || {}).map(([key, value]) => ({ key, value })));
  const [saving, setSaving] = useState(false);
  const isEdit = !!member;

  const save = async () => {
    if (!form.email.trim() || !form.name.trim()) return toast.error("Name and email required");
    setSaving(true);
    const attributes = {};
    attrs.forEach(a => { if (a.key.trim() && a.value.trim()) attributes[a.key.trim()] = a.value.trim(); });
    try {
      if (isEdit) await api.patch(`/team/${member.id}`, { name: form.name, notes: form.notes, attributes });
      else await api.post("/team", { email: form.email, name: form.name, notes: form.notes, attributes });
      toast.success(isEdit ? "Updated" : "Added"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="member-dialog">
      <div className="bg-white rounded-2xl border border-[var(--border)] max-w-lg w-full max-h-[90vh] overflow-y-auto scroll-thin shadow-xl">
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="label-caps mb-1">{isEdit ? "Edit" : "Add"} member</div>
            <h2 className="font-display text-xl font-medium">{isEdit ? member.name : "New team member"}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label-caps block mb-2">Email</label>
            <input data-testid="member-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} disabled={isEdit} className="input-base" />
          </div>
          <div>
            <label className="label-caps block mb-2">Name</label>
            <input data-testid="member-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-base" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-caps">Attributes</label>
              <button type="button" onClick={() => setAttrs([...attrs, { key: "", value: "" }])} className="text-xs text-[var(--primary)] hover:underline flex items-center gap-1"><Plus size={12} /> Add</button>
            </div>
            <div className="text-xs text-slate-500 mb-3">e.g. <span className="mono">department = Engineering</span> — auto-fills access rules.</div>
            {attrs.length === 0 && <div className="text-xs text-slate-400">No attributes.</div>}
            <div className="space-y-2">
              {attrs.map((a, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`attr-${i}`}>
                  <input placeholder="column" value={a.key} onChange={(e) => setAttrs(attrs.map((x, idx) => idx === i ? {...x, key: e.target.value} : x))} className="input-base flex-1 mono" />
                  <span className="text-slate-400">=</span>
                  <input placeholder="value" value={a.value} onChange={(e) => setAttrs(attrs.map((x, idx) => idx === i ? {...x, value: e.target.value} : x))} className="input-base flex-1 mono" />
                  <button type="button" onClick={() => setAttrs(attrs.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-[var(--danger)]"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="label-caps block mb-2">Notes</label>
            <textarea data-testid="member-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-base" rows={2} />
          </div>
        </div>
        <div className="p-6 border-t border-[var(--border)] flex justify-end gap-3 bg-slate-50/50">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="member-save" className="btn-primary"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
